
import { BaseRepository } from '@/shared/infrastructure/BaseRepository';

interface YoungSchemaResultData {
  id: string;
  userId: string;
  abandonment: number;
  mistrustAbuse: number;
  emotionalDeprivation: number;
  defectiveness: number;
  socialIsolation: number;
  dependence: number;
  vulnerability: number;
  enmeshment: number;
  failure: number;
  entitlement: number;
  insufficientSelfControl: number;
  subjugation: number;
  selfSacrifice: number;
  approvalSeeking: number;
  negativity: number;
  emotionalInhibition: number;
  unrelentingStandards: number;
  punitiveness: number;
  createdAt: Date;
  updatedAt: Date;
}

interface CreateYoungSchemaResultData {
  userId: string;
  abandonment: number;
  mistrustAbuse: number;
  emotionalDeprivation: number;
  defectiveness: number;
  socialIsolation: number;
  dependence: number;
  vulnerability: number;
  enmeshment: number;
  failure: number;
  entitlement: number;
  insufficientSelfControl: number;
  subjugation: number;
  selfSacrifice: number;
  approvalSeeking: number;
  negativity: number;
  emotionalInhibition: number;
  unrelentingStandards: number;
  punitiveness: number;
}

export class YoungSchemasRepository extends BaseRepository {
  
  async findById(id: string): Promise<YoungSchemaResultData | null> {
    const { data, error } = await this.getClient()
      .from('young_schema_results')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Erro ao buscar resultado por ID: ${error.message}`);
      return null;
    }

    return data ? this.mapToYoungSchemaResultData(data) : null;
  }

  async findByUserId(userId: string): Promise<YoungSchemaResultData[]> {
    const { data, error } = await this.getClient()
      .from('young_schema_results')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Erro ao buscar resultados por usuário: ${error.message}`);
    }

    return (data || []).map(item => this.mapToYoungSchemaResultData(item));
  }

  async create(data: CreateYoungSchemaResultData): Promise<YoungSchemaResultData> {
    const { data: createdData, error } = await this.getClient()
      .from('young_schema_results')
      .insert({
        user_id: data.userId,
        abandonment: data.abandonment,
        mistrust_abuse: data.mistrustAbuse,
        emotional_deprivation: data.emotionalDeprivation,
        defectiveness: data.defectiveness,
        social_isolation: data.socialIsolation,
        failure: data.failure,
        dependence: data.dependence,
        vulnerability: data.vulnerability,
        enmeshment: data.enmeshment,
        entitlement: data.entitlement,
        insufficient_self_control: data.insufficientSelfControl,
        subjugation: data.subjugation,
        self_sacrifice: data.selfSacrifice,
        approval_seeking: data.approvalSeeking,
        negativity: data.negativity,
        emotional_inhibition: data.emotionalInhibition,
        unrelenting_standards: data.unrelentingStandards,
        punitiveness: data.punitiveness
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Erro ao criar resultado: ${error.message}`);
    }

    return this.mapToYoungSchemaResultData(createdData);
  }

  async getLatestResult(userId: string): Promise<YoungSchemaResultData | null> {
    const { data, error } = await this.getClient()
      .from('young_schema_results')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`Erro ao buscar último resultado: ${error.message}`);
    }

    return data ? this.mapToYoungSchemaResultData(data) : null;
  }

  async getResultsHistory(userId: string, limit?: number): Promise<YoungSchemaResultData[]> {
    let query = this.getClient()
      .from('young_schema_results')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Erro ao buscar histórico de resultados: ${error.message}`);
    }

    return (data || []).map(item => this.mapToYoungSchemaResultData(item));
  }

  private mapToYoungSchemaResultData(data: any): YoungSchemaResultData {
    return {
      id: data.id,
      userId: data.user_id,
      abandonment: data.abandonment,
      mistrustAbuse: data.mistrust_abuse,
      emotionalDeprivation: data.emotional_deprivation,
      defectiveness: data.defectiveness,
      socialIsolation: data.social_isolation,
      failure: data.failure,
      dependence: data.dependence,
      vulnerability: data.vulnerability,
      enmeshment: data.enmeshment,
      entitlement: data.entitlement,
      insufficientSelfControl: data.insufficient_self_control,
      subjugation: data.subjugation,
      selfSacrifice: data.self_sacrifice,
      approvalSeeking: data.approval_seeking,
      negativity: data.negativity,
      emotionalInhibition: data.emotional_inhibition,
      unrelentingStandards: data.unrelenting_standards,
      punitiveness: data.punitiveness,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    };
  }
}
